import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { environment } from 'src/environments/environment';
import { Language, translations, getDirection } from '../thi-translations';
import { normalizeThiLang } from '../thi-lang.util';
import { getCampaignT, CampaignTranslations } from './thi-campaign-translations';
import { ThiCampaignAttributionService } from './thi-campaign-attribution.service';
import { ThiLandingAnalyticsService } from './thi-landing-analytics.service';

@Component({
  selector: 'app-thi-campaign-main',
  templateUrl: './thi-campaign-main.component.html',
  styleUrls: ['./thi-campaign-main.component.scss'],
})
export class ThiCampaignMainComponent implements OnInit, OnChanges, OnDestroy {
  @Input() lang: Language = 'ar';
  @Input() showStickyCta = false;

  @ViewChild('finalCta') finalCtaRef?: ElementRef<HTMLElement>;

  t = translations.ar;
  c: CampaignTranslations = getCampaignT('ar');
  dir: 'rtl' | 'ltr' = 'rtl';
  openFaqIndex = -1;
  hideStickyNearFooter = false;

  readonly demoVideoUrl = environment.campaignDemoVideoUrl ?? '';

  featureCards: { icon: string; title: string; desc: string }[] = [];
  whyItems: { icon: string; title: string; desc: string }[] = [];

  /** Icons for the six report metric cards (order matches c.reports.metrics). */
  readonly metricIcons: string[] = [
    'fa fa-line-chart',
    'fa fa-credit-card',
    'fa fa-balance-scale',
    'fa fa-pie-chart',
    'fa fa-exchange',
    'fa fa-area-chart',
  ];

  private footerObserver?: IntersectionObserver;

  constructor(
    private readonly attribution: ThiCampaignAttributionService,
    private readonly analytics: ThiLandingAnalyticsService,
  ) {}

  ngOnInit(): void {
    this.applyLang();
    this.setupFooterObserver();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['lang']) {
      this.applyLang();
    }
  }

  ngOnDestroy(): void {
    this.footerObserver?.disconnect();
  }

  get arrowIcon(): string {
    return this.dir === 'rtl' ? 'fa fa-arrow-left' : 'fa fa-arrow-right';
  }

  get registerParams(): Record<string, string> {
    return this.attribution.registerQueryParams();
  }

  toggleFaq(i: number): void {
    this.openFaqIndex = this.openFaqIndex === i ? -1 : i;
  }

  onStartFree(section: string, planId?: number): void {
    this.analytics.startFreeClick(this.lang, section, planId);
  }

  /** Mid-page CTAs: track click then smooth-scroll to packages. */
  scrollToPricing(section: string): void {
    this.analytics.startFreeClick(this.lang, section);
    if (typeof document === 'undefined') {
      return;
    }
    const el = document.getElementById('pricing');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  onWatchDemo(section: string): void {
    this.analytics.watchDemoClick(this.lang, section);
    const url = this.demoVideoUrl?.trim();
    if (url && typeof window !== 'undefined') {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  onPricingVisible(): void {
    this.analytics.pricingView(this.lang);
  }

  onPackageSelected(planId: number): void {
    this.analytics.packageSelected(this.lang, planId);
    this.onStartFree('pricing', planId);
  }

  private applyLang(): void {
    this.lang = normalizeThiLang(this.lang as unknown as string);
    this.t = translations[this.lang];
    this.c = getCampaignT(this.lang);
    this.dir = getDirection(this.lang);

    this.featureCards = [
      { icon: 'fa fa-file-text-o', title: this.c.features.invoicing, desc: this.c.features.invoicingDesc },
      { icon: 'fa fa-camera', title: this.c.features.expenses, desc: this.c.features.expensesDesc },
      { icon: 'fa fa-lightbulb-o', title: this.c.features.advisor, desc: this.c.features.advisorDesc },
      { icon: 'fa fa-bar-chart', title: this.c.features.reports, desc: this.c.features.reportsDesc },
    ];

    const whyIcons = ['fa fa-globe', 'fa fa-camera', 'fa fa-file-text-o', 'fa fa-line-chart', 'fa fa-magic', 'fa fa-th-large'];
    this.whyItems = this.c.whyThi.items.map((item, i) => ({
      icon: whyIcons[i] ?? 'fa fa-check',
      title: item.title,
      desc: item.desc,
    }));
  }

  private setupFooterObserver(): void {
    if (typeof IntersectionObserver === 'undefined') {
      return;
    }
    setTimeout(() => {
      const el = this.finalCtaRef?.nativeElement;
      if (!el) {
        return;
      }
      this.footerObserver = new IntersectionObserver(
        (entries) => {
          this.hideStickyNearFooter = entries.some((e) => e.isIntersecting);
        },
        { threshold: 0.15 },
      );
      this.footerObserver.observe(el);
    });
  }
}
